import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  commitSimulation,
  getApproval,
  listPendingApprovals,
  rejectSimulation,
  type ApprovalRecord,
  type AuditEntry,
  type ListPendingParams,
} from "@/api/approvals";

export const APPROVALS_PENDING_KEY = ["approvals", "pending"] as const;
export const APPROVALS_BELL_KEY = ["approvals", "pending", "summary"] as const;
export const APPROVAL_KEY = (id: number | null) => ["approval", id] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function usePendingApprovals(params: ListPendingParams = {}) {
  return useQuery<ApprovalRecord[], Error>({
    queryKey: [...APPROVALS_PENDING_KEY, params] as const,
    queryFn: () => listPendingApprovals(params),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
    retry: noRetryOn401,
  });
}

export function useApproval(simulationId: number | null) {
  return useQuery<ApprovalRecord, Error>({
    queryKey: APPROVAL_KEY(simulationId),
    queryFn: () => getApproval(simulationId as number),
    enabled: simulationId !== null,
    retry: noRetryOn401,
    staleTime: 15_000,
  });
}

interface MutateVars {
  simulationId: number;
  reason: string;
}

function invalidateAfterDecision(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: APPROVALS_PENDING_KEY });
  void qc.invalidateQueries({ queryKey: APPROVALS_BELL_KEY });
  void qc.invalidateQueries({ queryKey: ["projects"] });
  void qc.invalidateQueries({ queryKey: ["project"] });
  void qc.invalidateQueries({ queryKey: ["project-tasks"] });
  void qc.invalidateQueries({ queryKey: ["project-risks"] });
  void qc.invalidateQueries({ queryKey: ["project-audit"] });
}

export function useCommitSimulation() {
  const qc = useQueryClient();
  return useMutation<AuditEntry, Error, MutateVars>({
    mutationFn: ({ simulationId, reason }) => commitSimulation(simulationId, reason),
    onSuccess: () => invalidateAfterDecision(qc),
  });
}

export function useRejectSimulation() {
  const qc = useQueryClient();
  return useMutation<AuditEntry, Error, MutateVars>({
    mutationFn: ({ simulationId, reason }) => rejectSimulation(simulationId, reason),
    onSuccess: () => invalidateAfterDecision(qc),
  });
}
